import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { api } from '../api';
import ModuleAccessTab from './ModuleAccessTab';
import ModulePermissionsTab from './ModulePermissionsTab';
import ModuleWorkflowTab from './ModuleWorkflowTab';
import ModuleNotificationsTab from './ModuleNotificationsTab';

/**
 * Reusable per-module setup page shell.
 * Renders 5 tabs: Access, Permissions, Workflow, Notifications, Config.
 *
 * Access rules drive the scope for all downstream tabs — positions/users not
 * granted access don't appear as options in Permissions/Workflow/Notifications.
 */
export default function ModuleSetupPage({ moduleKey, moduleLabel, configComponent }) {
  const { isConfigurator } = useAuth();
  const [activeTab, setActiveTab] = useState('access');
  const [scope, setScope] = useState({ positions: [], users: [] });
  const [scopeLoading, setScopeLoading] = useState(true);

  const loadScope = useCallback(async () => {
    setScopeLoading(true);
    try {
      const data = await api.getModuleScope(moduleKey);
      setScope({
        positions: Array.isArray(data?.positions) ? data.positions : [],
        users: Array.isArray(data?.users) ? data.users : [],
      });
    } catch (err) {
      console.error('Failed to load module scope:', err);
      setScope({ positions: [], users: [] });
    } finally {
      setScopeLoading(false);
    }
  }, [moduleKey]);

  useEffect(() => { loadScope(); }, [loadScope]);

  if (!isConfigurator(moduleKey)) {
    return (
      <div className="admin-page">
        <div className="page-header"><h2>Access Denied</h2></div>
        <p>You do not have permission to configure this module.</p>
      </div>
    );
  }

  const tabs = [
    { key: 'access', label: 'Access' },
    { key: 'permissions', label: 'Permissions' },
    { key: 'workflow', label: 'Workflow' },
    { key: 'notifications', label: 'Notifications' },
    { key: 'config', label: 'Config' },
  ];

  return (
    <div className="admin-page">
      <div className="page-header">
        <h2>{moduleLabel} Setup</h2>
        <p style={{ margin: '4px 0 0', fontSize: 13, color: 'var(--text-muted)' }}>
          Start with <strong>Access</strong> — it controls who appears in Permissions, Workflow, and Notifications.
        </p>
      </div>

      {/* Tab bar — matches Teams page style */}
      <div style={{
        display: 'flex', gap: 0,
        borderBottom: '2px solid var(--border-subtle)',
        marginBottom: 24,
      }}>
        {tabs.map(tab => {
          const isActive = activeTab === tab.key;
          return (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              style={{
                padding: '10px 22px',
                fontSize: 14,
                fontWeight: isActive ? 700 : 500,
                color: isActive ? 'var(--brand-gold)' : 'var(--text-muted)',
                background: 'none',
                border: 'none',
                borderBottom: isActive ? '2px solid var(--brand-gold)' : '2px solid transparent',
                marginBottom: -2,
                cursor: 'pointer',
                transition: 'all 0.15s ease',
              }}
            >
              {tab.label}
            </button>
          );
        })}
      </div>

      <div>
        {activeTab === 'access' && (
          <ModuleAccessTab moduleKey={moduleKey} onAccessChanged={loadScope} />
        )}
        {activeTab === 'permissions' && (
          <ModulePermissionsTab moduleKey={moduleKey} scope={scope} scopeLoading={scopeLoading} />
        )}
        {activeTab === 'workflow' && (
          <ModuleWorkflowTab moduleKey={moduleKey} scope={scope} scopeLoading={scopeLoading} />
        )}
        {activeTab === 'notifications' && (
          <ModuleNotificationsTab moduleKey={moduleKey} scope={scope} scopeLoading={scopeLoading} />
        )}
        {activeTab === 'config' && (configComponent || <p style={{ color: 'var(--text-muted)' }}>No module-specific configuration.</p>)}
      </div>
    </div>
  );
}
